import React, { useEffect, useState } from 'react';
import { CreditCard, Ticket, Loader2, Plus, Power } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from '../../utils/api';

/** Super-Admin Monetization — subscription plan pricing + coupon management. */
export default function MonetizationPanel() {
  const [plans, setPlans] = useState(null);
  const [savingPlans, setSavingPlans] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', discount_type: 'percent', value: '', applies_to: 'all', max_uses: '' });

  useEffect(() => {
    apiCall('/api/admin/finance/subscription-plans').then((d) => setPlans(d.plans)).catch(() => {});
    loadCoupons();
  }, []);

  const loadCoupons = () =>
    apiCall('/api/admin/finance/coupons').then((d) => setCoupons(d.coupons || [])).catch(() => {});

  const savePlans = async () => {
    setSavingPlans(true);
    try {
      const d = await apiCall('/api/admin/finance/subscription-plans', {
        method: 'PUT',
        body: JSON.stringify({ plans: plans.map((p) => ({ ...p, price_monthly: Number(p.price_monthly) || 0 })) }),
      });
      setPlans(d.plans);
      toast.success('Subscription plans saved');
    } catch (e) {
      toast.error(e?.message || 'Failed to save plans');
    } finally { setSavingPlans(false); }
  };

  const createCoupon = async () => {
    setCreating(true);
    try {
      await apiCall('/api/admin/finance/coupons', {
        method: 'POST',
        body: JSON.stringify({
          code: form.code,
          discount_type: form.discount_type,
          value: Number(form.value),
          applies_to: form.applies_to,
          max_uses: form.max_uses ? Number(form.max_uses) : null,
        }),
      });
      toast.success(`Coupon ${form.code.toUpperCase()} created`);
      setForm({ code: '', discount_type: 'percent', value: '', applies_to: 'all', max_uses: '' });
      loadCoupons();
    } catch (e) {
      toast.error(e?.message || 'Failed to create coupon');
    } finally { setCreating(false); }
  };

  const toggleCoupon = async (c) => {
    try {
      await apiCall(`/api/admin/finance/coupons/${c._id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !c.active }),
      });
      setCoupons((prev) => prev.map((x) => (x._id === c._id ? { ...x, active: !c.active } : x)));
    } catch (e) {
      toast.error(e?.message || 'Failed to update coupon');
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="monetization-panel">
      <div className="flex items-center gap-2 mb-4">
        <CreditCard className="w-5 h-5 text-emerald-600" />
        <h3 className="font-semibold text-gray-900">Subscriptions &amp; Coupons</h3>
        <span className="text-xs text-gray-400 ml-auto">Financial OS · Super Admin</span>
      </div>

      {/* Subscription plans */}
      <div className="mb-5">
        <h4 className="text-sm font-semibold text-gray-800 mb-2">Subscription Plans</h4>
        {!plans ? (
          <p className="text-xs text-gray-400">Loading plans…</p>
        ) : (
          <div className="space-y-2">
            {plans.map((p, i) => (
              <div key={p.plan_id} className="flex items-center gap-3 text-sm" data-testid={`plan-row-${p.plan_id}`}>
                <span className="flex-1 truncate font-medium text-gray-700">{p.name}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${p.role === 'agent' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'}`}>{p.role}</span>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">$</span>
                  <input
                    type="number" min="0" step="1"
                    value={p.price_monthly}
                    onChange={(e) => setPlans(plans.map((x, j) => (j === i ? { ...x, price_monthly: e.target.value } : x)))}
                    className="w-20 px-2 py-1 border border-gray-200 rounded-lg text-sm"
                    data-testid={`plan-price-${p.plan_id}`}
                  />
                  <span className="text-xs text-gray-400">/mo</span>
                </div>
              </div>
            ))}
            <button
              onClick={savePlans}
              disabled={savingPlans}
              className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40"
              data-testid="save-plans-btn"
            >
              {savingPlans && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save Plans
            </button>
          </div>
        )}
      </div>

      {/* Coupons */}
      <div className="pt-4 border-t border-gray-100">
        <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5"><Ticket className="w-4 h-4 text-amber-500" /> Coupons</h4>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
          <input
            placeholder="CODE"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm uppercase"
            data-testid="coupon-code-input"
          />
          <select
            value={form.discount_type}
            onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
            className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
            data-testid="coupon-type-select"
          >
            <option value="percent">% off</option>
            <option value="fixed">$ off</option>
          </select>
          <input
            type="number" min="0" placeholder="Value"
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
            data-testid="coupon-value-input"
          />
          <select
            value={form.applies_to}
            onChange={(e) => setForm({ ...form, applies_to: e.target.value })}
            className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
            data-testid="coupon-applies-select"
          >
            <option value="all">All</option>
            <option value="marketplace">Marketplace</option>
            <option value="subscription">Subscriptions</option>
          </select>
          <input
            type="number" min="1" placeholder="Max uses"
            value={form.max_uses}
            onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
            className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
            data-testid="coupon-maxuses-input"
          />
          <button
            onClick={createCoupon}
            disabled={creating || !form.code || !form.value}
            className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-40"
            data-testid="create-coupon-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
          </button>
        </div>
        {coupons.length === 0 ? (
          <p className="text-xs text-gray-400">No coupons yet.</p>
        ) : (
          <div className="max-h-56 overflow-y-auto divide-y divide-gray-50 border border-gray-100 rounded-xl" data-testid="coupons-list">
            {coupons.map((c) => (
              <div key={c._id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`coupon-row-${c.code}`}>
                <span className="font-mono font-bold text-gray-800">{c.code}</span>
                <span className="text-gray-500">{c.discount_type === 'percent' ? `${c.value}% off` : `$${Number(c.value).toFixed(2)} off`}</span>
                <span className="text-[11px] px-2 py-0.5 bg-gray-50 text-gray-500 rounded-full">{c.applies_to}</span>
                <span className="text-xs text-gray-400 ml-auto">{c.used_count || 0}{c.max_uses ? `/${c.max_uses}` : ''} used</span>
                <button
                  onClick={() => toggleCoupon(c)}
                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg ${c.active ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                  data-testid={`toggle-coupon-${c.code}`}
                >
                  <Power className="w-3 h-3" /> {c.active ? 'Active' : 'Inactive'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
